import { useEffect, useRef, useState } from "react";

// Local copy of the same scroll-through formula used in capabilitiesKit.jsx
// (kept independent rather than cross-importing, to avoid a circular
// module dependency between the two capability-only files).
function useScrollThrough(ref) {
  const [progress, setProgress] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setReduceMotion(reduce);
    if (reduce) {
      setProgress(1);
      return undefined;
    }
    const el = ref.current;
    if (!el) return undefined;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || 1;
        const total = rect.height + vh;
        const passed = vh - rect.top;
        setProgress(Math.max(0, Math.min(1, passed / total)));
        raf = null;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ref]);

  return { progress, reduceMotion };
}

/* Isolated to /design-preview/capabilities. Real CSS-3D spatial scenes per
   capability — no WebGL/Three.js dependency added (keeps this out of the
   production bundle risk entirely), no orbs/particles/neon. Every scene is
   built from perspective + preserve-3d + translateZ/rotate on real DOM
   nodes, driven by pointer position and scroll-into-view. */

function useStageTilt(maxDeg = 9) {
  const ref = useRef(null);
  const raf = useRef(null);
  const disabled = useRef(false);

  useEffect(() => {
    disabled.current =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      window.matchMedia("(pointer: coarse)").matches;
  }, []);

  const onMouseMove = (e) => {
    const el = ref.current;
    if (!el || disabled.current) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => {
      el.style.setProperty("--dpcap-stage-rx", `${(-py * maxDeg).toFixed(2)}deg`);
      el.style.setProperty("--dpcap-stage-ry", `${(px * maxDeg).toFixed(2)}deg`);
      raf.current = null;
    });
  };

  const onMouseLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--dpcap-stage-rx", "0deg");
    el.style.setProperty("--dpcap-stage-ry", "0deg");
  };

  return { ref, onMouseMove, onMouseLeave };
}

function useAssemble(threshold = 0.4) {
  const ref = useRef(null);
  const [on, setOn] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setOn(true);
      return undefined;
    }
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setOn(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return { ref, on };
}

function useCombinedRef(...refs) {
  return (node) => {
    refs.forEach((r) => {
      if (r) r.current = node;
    });
  };
}

/* ---------------------------------------------------------------------- */
/* Stack — layers separate in Z and reassemble. WEBSITE, SAAS.            */
/* ---------------------------------------------------------------------- */

export function LayerStack({ layers }) {
  const tilt = useStageTilt(11);
  const assemble = useAssemble();
  const setRef = useCombinedRef(tilt.ref, assemble.ref);

  return (
    <div
      className={`dpcap-stage dpcap-stack${assemble.on ? " is-on" : ""}`}
      ref={setRef}
      onMouseMove={tilt.onMouseMove}
      onMouseLeave={tilt.onMouseLeave}
    >
      <div className="dpcap-stack__world">
        {layers.map((layer, i) => (
          <div
            key={layer.label}
            className="dpcap-stack__layer"
            style={{
              "--sx": `${layer.sx}px`,
              "--sy": `${layer.sy}px`,
              "--sz": `${layer.sz}px`,
              "--srot": `${layer.srot}deg`,
              "--rx": `${i * 30 - ((layers.length - 1) * 30) / 2}px`,
              "--ry": `${i * 44 - ((layers.length - 1) * 44) / 2}px`,
              "--rz": `${i * 40}px`,
              transitionDelay: `${i * 100}ms`,
            }}
          >
            <span className="dpcap-stack__bar">
              <i /><i /><i />
            </span>
            <span className="dpcap-stack__label">{layer.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Pipeline — flat/collapsed planes separate into a depth-ordered          */
/* architecture, connected by a drawn line. WEB DEV, AI, SEARCH.          */
/* ---------------------------------------------------------------------- */

export function DepthPipeline({ stages }) {
  const tilt = useStageTilt(9);
  const assemble = useAssemble();
  const setRef = useCombinedRef(tilt.ref, assemble.ref);
  const n = stages.length;

  return (
    <div
      className={`dpcap-stage dpcap-pipeline${assemble.on ? " is-on" : ""}`}
      ref={setRef}
      onMouseMove={tilt.onMouseMove}
      onMouseLeave={tilt.onMouseLeave}
    >
      <svg className="dpcap-pipeline__link" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <path d="M14,84 L90,18" />
      </svg>
      <div className="dpcap-pipeline__world">
        {stages.map((label, i) => (
          <div
            key={label}
            className="dpcap-pipeline__plane"
            style={{
              "--rx": `${14 + (i / Math.max(1, n - 1)) * 76}%`,
              "--ry": `${84 - (i / Math.max(1, n - 1)) * 66}%`,
              "--rz": `${-i * 46}px`,
              transitionDelay: `${i * 90}ms`,
            }}
          >
            <span className="dpcap-pipeline__n">0{i + 1}</span>
            <span className="dpcap-pipeline__label">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Module grid — small connected nodes in a tilted plane, relationships    */
/* highlight on hover. ADMIN / BUSINESS SYSTEM.                           */
/* ---------------------------------------------------------------------- */

const GRID_POS = [
  { x: 20, y: 22 },
  { x: 60, y: 16 },
  { x: 85, y: 42 },
  { x: 15, y: 62 },
  { x: 52, y: 58 },
  { x: 82, y: 78 },
];

export function ModuleGrid({ nodes }) {
  const tilt = useStageTilt(10);
  const assemble = useAssemble();
  const setRef = useCombinedRef(tilt.ref, assemble.ref);
  const [hover, setHover] = useState(null);
  const positioned = nodes.map((label, i) => ({ label, ...GRID_POS[i % GRID_POS.length] }));

  return (
    <div
      className={`dpcap-stage dpcap-modgrid${assemble.on ? " is-on" : ""}`}
      ref={setRef}
      onMouseMove={tilt.onMouseMove}
      onMouseLeave={tilt.onMouseLeave}
    >
      <svg className="dpcap-modgrid__world" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        {positioned.map((a, i) =>
          positioned.slice(i + 1).map((b) => (
            <line
              key={`${a.label}-${b.label}`}
              x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              className="dpcap-modgrid__link"
              style={{ opacity: !hover || hover === a.label || hover === b.label ? undefined : 0.06 }}
            />
          ))
        )}
        {positioned.map((node) => (
          <g
            key={node.label}
            transform={`translate(${node.x} ${node.y})`}
            className="dpcap-modgrid__node"
            style={{ opacity: !hover || hover === node.label ? 1 : 0.35 }}
            onMouseEnter={() => setHover(node.label)}
            onMouseLeave={() => setHover(null)}
          >
            <circle r="3.6" />
            <text y="7.5">{node.label}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Signal flow — trigger branches into workflow/action paths; a signal     */
/* travels and forks on hover. AUTOMATION.                                */
/* ---------------------------------------------------------------------- */

export function SignalFlow({ trigger, branches, actions }) {
  const tilt = useStageTilt(7);
  const assemble = useAssemble();
  const setRef = useCombinedRef(tilt.ref, assemble.ref);
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    setReduceMotion(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  const branchYs = [26, 50, 74].slice(0, Math.max(branches.length, 1));
  const actionYs = actions.map((_, i) => 14 + i * (72 / Math.max(1, actions.length - 1 || 1)));

  return (
    <div
      className={`dpcap-stage dpcap-sigflow${assemble.on ? " is-on" : ""}`}
      ref={setRef}
      onMouseMove={tilt.onMouseMove}
      onMouseLeave={tilt.onMouseLeave}
    >
      <div className="dpcap-sigflow__world">
        <svg className="dpcap-sigflow__svg" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {branchYs.map((y, i) => (
            <path key={`t-${i}`} className="dpcap-sigflow__path" d={`M14,50 C28,50 26,${y} 42,${y}`} />
          ))}
          {branchYs.map((y, bi) =>
            actionYs.map((ay, ai) =>
              (bi + ai) % 2 === 0 ? (
                <path key={`b-${bi}-${ai}`} className="dpcap-sigflow__path dpcap-sigflow__path--thin" d={`M46,${y} C64,${y} 66,${ay} 84,${ay}`} />
              ) : null
            )
          )}
          {!reduceMotion ? (
            <circle r="1.4" className="dpcap-sigflow__signal">
              <animateMotion dur="2.4s" repeatCount="indefinite" path="M14,50 C28,50 26,26 42,26 C60,26 62,50 84,50" />
            </circle>
          ) : null}
        </svg>
        <div className="dpcap-sigflow__node dpcap-sigflow__node--trigger" style={{ top: "50%" }}>{trigger}</div>
        {branches.map((b, i) => (
          <div key={b} className="dpcap-sigflow__node" style={{ top: `${branchYs[i]}%`, left: "42%" }}>{b}</div>
        ))}
        {actions.map((a, i) => (
          <div key={a} className="dpcap-sigflow__node dpcap-sigflow__node--action" style={{ top: `${actionYs[i]}%`, left: "84%" }}>{a}</div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Dispatcher                                                              */
/* ---------------------------------------------------------------------- */

const SCENE = {
  website: { kind: "stack", layers: [
    { label: "HEADER / NAV", sx: -70, sy: -46, sz: -130, srot: -16 },
    { label: "CONTENT", sx: 66, sy: 8, sz: 40, srot: 12 },
    { label: "FOOTER", sx: -34, sy: 70, sz: 120, srot: -9 },
  ] },
  saas: { kind: "stack", layers: [
    { label: "TENANT A", sx: -80, sy: -30, sz: -110, srot: -20 },
    { label: "TENANT B", sx: 74, sy: -10, sz: 30, srot: 16 },
    { label: "TENANT C", sx: -50, sy: 60, sz: 100, srot: -12 },
    { label: "CORE PLATFORM", sx: 40, sy: 70, sz: 150, srot: 8 },
  ] },
  webdev: { kind: "pipeline", stages: ["FRONTEND", "API", "BACKEND", "DATA"] },
  ai: { kind: "pipeline", stages: ["DATA", "CONTEXT", "MODEL", "RESULT"] },
  search: { kind: "pipeline", stages: ["CONTENT", "STRUCTURED DATA", "SEO · AEO · GEO", "SEARCH / AI", "USER"] },
  admin: { kind: "grid", nodes: ["CMS", "CRM", "USERS", "ORDERS", "DATA", "LOGS"] },
  automation: { kind: "flow", trigger: "TRIGGER", branches: ["ALERT", "UPDATE"], actions: ["NOTIFY", "SYNC", "LOG"] },
};

/* ---------------------------------------------------------------------- */
/* System assembly — replaces the flat "01 02 03 04 05 + line" PROCESS     */
/* row. Five real blocks (same text as before) scroll-scrub from scattered */
/* into an ascending, depth-ordered shelf as the reader scrolls past —     */
/* "the system assembling itself", not decoration layered on old circles.  */
/* Desktop-first composition; mobile collapses to a plain stacked list     */
/* via CSS (see capabilities.css), not a scaled-down 3D scene.             */
/* ---------------------------------------------------------------------- */

const ASSEMBLY_POS = [
  { x: 9, y: 66, z: -70 },
  { x: 28, y: 52, z: -30 },
  { x: 50, y: 41, z: 15 },
  { x: 72, y: 32, z: 60 },
  { x: 91, y: 25, z: 105 },
];

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function clamp01(v) {
  return Math.max(0, Math.min(1, v));
}

export function SystemAssembly({ steps }) {
  const tilt = useStageTilt(6);
  const stageRef = useRef(null);
  const setRef = useCombinedRef(tilt.ref, stageRef);
  const { progress, reduceMotion } = useScrollThrough(stageRef);
  const n = steps.length;

  const pathD = ASSEMBLY_POS.slice(0, n)
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`)
    .join(" ");
  const pathLen = 105;

  return (
    <div
      className="dpcap-assembly"
      ref={setRef}
      onMouseMove={tilt.onMouseMove}
      onMouseLeave={tilt.onMouseLeave}
    >
      <svg className="dpcap-assembly__link" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
        <path
          d={pathD}
          style={{
            strokeDasharray: pathLen,
            strokeDashoffset: reduceMotion ? 0 : pathLen * (1 - progress),
          }}
        />
      </svg>
      <div className="dpcap-assembly__world">
        {steps.map((step, i) => {
          const windowStart = i / n;
          const windowEnd = (i + 0.75) / n;
          const local = reduceMotion ? 1 : clamp01((progress - windowStart) / (windowEnd - windowStart));
          const rest = ASSEMBLY_POS[i];
          const scatterSign = i % 2 === 0 ? -1 : 1;
          const x = lerp(rest.x + scatterSign * 16, rest.x, local);
          const y = lerp(rest.y - 34, rest.y, local);
          const z = lerp(-190, rest.z, local);
          const rot = lerp(scatterSign * 22, 0, local);
          return (
            <div
              key={step.id}
              className={`dpcap-assembly__block${local > 0.6 ? " is-settled" : ""}`}
              style={{
                left: `${x}%`,
                top: `${y}%`,
                transform: `translate(-50%, -50%) translateZ(${z}px) rotateY(${rot}deg)`,
                opacity: lerp(0.06, 1, local),
              }}
            >
              <span className="dpcap-assembly__n">{String(i + 1).padStart(2, "0")}</span>
              <h4>{step.title}</h4>
              <p>{step.body}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function CapabilityStage({ id }) {
  const cfg = SCENE[id] || SCENE.website;
  if (cfg.kind === "stack") return <LayerStack layers={cfg.layers} />;
  if (cfg.kind === "pipeline") return <DepthPipeline stages={cfg.stages} />;
  if (cfg.kind === "grid") return <ModuleGrid nodes={cfg.nodes} />;
  if (cfg.kind === "flow") return <SignalFlow trigger={cfg.trigger} branches={cfg.branches} actions={cfg.actions} />;
  return null;
}
